import type { CSSProperties, ReactNode } from 'react';
import { RiCheckboxFill, RiCheckboxBlankLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';

/**
 * Read-only renderer for the device-info description (TipTap / ProseMirror JSON, §6.8). Builds React elements —
 * no HTML injection; links are limited to http(s)/mailto/tel, colours to hex/rgb. Unknown nodes render their
 * children. Viewer typography (599:37333): heading SB 15 White, paragraph R 14/21 White/800, bullets with 4 px dots.
 */

interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}
interface PMNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: PMMark[];
  content?: PMNode[];
}

const isNode = (v: unknown): v is PMNode => typeof v === 'object' && v !== null && typeof (v as PMNode).type === 'string';
const safeHref = (href: unknown) => (typeof href === 'string' && /^(https?:|mailto:|tel:|\/)/i.test(href.trim()) ? href.trim() : null);
const safeColor = (c: unknown) => (typeof c === 'string' && /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\))$/i.test(c.trim()) ? c.trim() : undefined);
const align = (attrs?: Record<string, unknown>): CSSProperties | undefined => {
  const a = attrs?.textAlign;
  return a === 'center' || a === 'right' || a === 'justify' ? { textAlign: a } : undefined;
};

function renderText(node: PMNode, key: number): ReactNode {
  let out: ReactNode = node.text ?? '';
  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
      case 'strong':
        out = <strong className="font-semibold text-white">{out}</strong>;
        break;
      case 'italic':
      case 'em':
        out = <em>{out}</em>;
        break;
      case 'underline':
        out = <u>{out}</u>;
        break;
      case 'strike':
        out = <s>{out}</s>;
        break;
      case 'code':
        out = <code className="rounded-4 bg-white-100 px-4 font-mono text-[0.85em]">{out}</code>;
        break;
      case 'link': {
        const href = safeHref(mark.attrs?.href);
        if (href) out = <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-1000 underline underline-offset-2">{out}</a>;
        break;
      }
      case 'textStyle':
        if (safeColor(mark.attrs?.color)) out = <span style={{ color: safeColor(mark.attrs?.color) }}>{out}</span>;
        break;
      case 'highlight':
        out = <mark className="rounded-2 bg-yellow-300 px-2 text-inherit">{out}</mark>;
        break;
      default:
        break;
    }
  }
  return <span key={key}>{out}</span>;
}

function children(node: PMNode): ReactNode[] {
  return (node.content ?? []).filter(isNode).map((c, i) => renderNode(c, i));
}

function renderNode(node: PMNode, key: number): ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, key);
    case 'paragraph':
      return (
        <p key={key} style={align(node.attrs)} className="text-sm leading-21 tracking-normal text-white-800">
          {children(node)}
        </p>
      );
    case 'heading': {
      // document headings sit under the modal's section titles → h4…h6
      const level = Number(node.attrs?.level ?? 2);
      const Tag = level <= 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
      return (
        <Tag key={key} style={align(node.attrs)} className={cn('font-semibold text-white', level <= 1 ? 'text-lg' : 'text-15')}>
          {children(node)}
        </Tag>
      );
    }
    case 'bulletList':
      return (
        <ul key={key} className="flex flex-col gap-8">
          {(node.content ?? []).filter(isNode).map((item, i) => (
            <li key={i} className="flex gap-8 text-sm leading-21 text-white-800">
              <span aria-hidden className="mt-9 size-4 shrink-0 rounded-full bg-white-600" />
              <div className="min-w-0 [&>p]:inline">{children(item)}</div>
            </li>
          ))}
        </ul>
      );
    case 'orderedList': {
      const start = Number(node.attrs?.start ?? 1);
      return (
        <ol key={key} className="flex flex-col gap-8">
          {(node.content ?? []).filter(isNode).map((item, i) => (
            <li key={i} className="flex gap-8 text-sm leading-21 text-white-800">
              <span className="shrink-0 tabular-nums text-white-600">{start + i}.</span>
              <div className="min-w-0 [&>p]:inline">{children(item)}</div>
            </li>
          ))}
        </ol>
      );
    }
    case 'taskList':
      return (
        <ul key={key} className="flex flex-col gap-8">
          {(node.content ?? []).filter(isNode).map((item, i) => {
            const Icon = item.attrs?.checked ? RiCheckboxFill : RiCheckboxBlankLine;
            return (
              <li key={i} className="flex gap-8 text-sm leading-21 text-white-800">
                <Icon aria-hidden className={cn('mt-2 size-16 shrink-0', item.attrs?.checked ? 'text-dark-blue-1000' : 'text-white-600')} />
                <div className="min-w-0 [&>p]:inline">{children(item)}</div>
              </li>
            );
          })}
        </ul>
      );
    case 'listItem':
    case 'taskItem':
      return <div key={key}>{children(node)}</div>;
    case 'blockquote':
      return (
        <blockquote key={key} className="border-l-2 border-white-200 pl-12 text-white-700 italic">
          {children(node)}
        </blockquote>
      );
    case 'codeBlock':
      return (
        <pre key={key} className="overflow-x-auto rounded-8 bg-white-50 p-10 font-mono text-xs text-white-900">
          {(node.content ?? []).map((c) => c.text ?? '').join('')}
        </pre>
      );
    case 'horizontalRule':
      return <hr key={key} className="border-white-100" />;
    case 'hardBreak':
      return <br key={key} />;
    case 'image': {
      const src = safeHref(node.attrs?.src);
      return src ? <img key={key} src={src} alt={typeof node.attrs?.alt === 'string' ? node.attrs.alt : ''} className="max-w-full rounded-8" /> : null;
    }
    case 'table':
      return (
        <table key={key} className="w-full border-collapse text-sm text-white-800">
          <tbody>{children(node)}</tbody>
        </table>
      );
    case 'tableRow':
      return <tr key={key}>{children(node)}</tr>;
    case 'tableHeader':
      return (
        <th key={key} className="border border-white-100 px-8 py-4 text-left font-semibold text-white">
          {children(node)}
        </th>
      );
    case 'tableCell':
      return (
        <td key={key} className="border border-white-100 px-8 py-4">
          {children(node)}
        </td>
      );
    default:
      return <div key={key}>{children(node)}</div>;
  }
}

/** Plain text of a document (character counters, PDF titles, empty checks). */
export function richTextPlain(doc: unknown): string {
  if (!isNode(doc)) return '';
  const walk = (n: PMNode): string => (n.type === 'text' ? (n.text ?? '') : (n.content ?? []).filter(isNode).map(walk).join(n.type === 'doc' ? '\n' : ' '));
  return walk(doc).trim();
}

export function RichTextView({ doc, className }: { doc: unknown; className?: string }) {
  if (!isNode(doc)) return null;
  return <div className={cn('flex flex-col gap-8', className)}>{children(doc)}</div>;
}
